"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Container } from '@/components/design-system';
import { Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { ThemeToggle } from '@/components/theme-toggle';
import { useLocaleNavigation } from '@/lib/navigation';
import { CATEGORIES } from '@/constants/categories';

export default function Header() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
    const t = useTranslations();
    const { getNavigationPath } = useLocaleNavigation();
    const locale = useLocale();
    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

    const toggleCategory = (categoryId: string) => {
        setExpandedCategories(prev =>
            prev.includes(categoryId)
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        );
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
        setExpandedCategories([]);
    };

    return (
        <header className="sticky top-0 z-50 w-full border-b border-gray-800 bg-black/95 backdrop-blur supports-[backdrop-filter]:bg-black/90 shadow-xl">
            <Container>
                <div className="flex h-14 items-center justify-between">
                    {/* Logo */}
                    <Link href={getNavigationPath('home')} className="flex items-center space-x-2">
                        <Image src="/logo.svg" alt="FakeXGenerator" width={32} height={32} className="h-8 w-auto" priority />
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center space-x-6">
                        {/* Categories */}
                        {Object.values(CATEGORIES).map((category) => (
                            <div key={category.id} className="relative group">
                                <button className="flex items-center space-x-2 text-sm font-medium text-gray-200 transition-all duration-200 hover:text-white group-hover:text-white px-3 py-2 rounded-lg hover:bg-white/5">
                                    <span>{category.name[locale]}</span>
                                    <ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
                                </button>

                                {/* Invisible bridge to prevent dropdown from closing */}
                                <div className="absolute top-full left-0 w-full h-3 bg-transparent"></div>

                                {/* Category Dropdown Menu */}
                                <div className="absolute top-full left-0 mt-0 w-72 bg-white backdrop-blur-md border border-gray-200/50 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ease-out z-50">
                                    {/* Content */}
                                    <div className="p-2">
                                        <div className="grid gap-0.5">
                                            {category.generators.slice(0, 6).map((generator) => (
                                                <Link
                                                    key={generator.id}
                                                    href={getNavigationPath(generator.id)}
                                                    className="flex items-center space-x-2.5 p-2 rounded-md text-gray-700 hover:text-gray-900 transition-all duration-200 hover:bg-gray-100/80 group/item"
                                                >
                                                    <div className="w-6 h-6 flex items-center justify-center rounded-md bg-gray-100 text-gray-500 group-hover/item:text-gray-700 group-hover/item:bg-gray-200 transition-all duration-200">
                                                        {generator.icon}
                                                    </div>
                                                    <div className="flex-1">
                                                        <span className="font-medium text-xs">{generator.name[locale]}</span>
                                                    </div>
                                                    <div className="opacity-0 group-hover/item:opacity-100 transition-opacity duration-200">
                                                        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                        </svg>
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>

                                        {/* View All Button */}
                                        <div className="mt-2 pt-2 border-t border-gray-200/50">
                                            <Link
                                                href={getNavigationPath(category.id)}
                                                className="flex items-center justify-center space-x-2 w-full p-2 rounded-md text-gray-600 hover:text-gray-800 transition-all duration-200 font-medium text-xs hover:bg-gray-100/80"
                                            >
                                                <span>{t('header.viewAllGenerators')}</span>
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        <Link href={getNavigationPath('blog')} className="flex items-center space-x-2 text-sm font-medium text-gray-200 transition-all duration-200 hover:text-white group-hover:text-white px-3 py-2 rounded-lg hover:bg-white/5">
                            <span>Blog</span>
                        </Link>
                    </nav>

                    {/* Desktop Actions */}
                    <div className="hidden md:flex items-center space-x-4">
                        <ThemeToggle />
                        <LanguageSwitcher variant="button" />
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={toggleMenu}
                        className="md:hidden p-2 rounded-md text-gray-200 hover:bg-gray-900 hover:text-white transition-colors"
                    >
                        {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>

                {/* Mobile Navigation */}
                {isMenuOpen && (
                    <div className="md:hidden py-4 border-t border-gray-800 bg-gray-900/80">
                        <nav className="flex flex-col space-y-2">
                            {/* Mobile Categories */}
                            {Object.values(CATEGORIES).map((category) => {
                                const isExpanded = expandedCategories.includes(category.id);
                                return (
                                    <div key={category.id} className="space-y-1">
                                        {/* Category Header */}
                                        <button
                                            onClick={() => toggleCategory(category.id)}
                                            className="w-full flex items-center justify-between p-4 rounded-xl text-gray-200 hover:bg-white/5 hover:text-white transition-all duration-200 border border-transparent hover:border-white/10"
                                        >
                                            <div className="flex items-center space-x-3">
                                                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-gray-400">
                                                    {category.icon}
                                                </div>
                                                <div>
                                                    <span className="text-sm font-medium">{category.name[locale]}</span>
                                                    <p className="text-xs text-gray-400 mt-0.5">{category.generators.length} generators</p>
                                                </div>
                                            </div>
                                            {isExpanded ? (
                                                <ChevronDown className="h-4 w-4 text-gray-400 transition-transform duration-200" />
                                            ) : (
                                                <ChevronRight className="h-4 w-4 text-gray-400 transition-transform duration-200" />
                                            )}
                                        </button>

                                        {/* Category Generators */}
                                        {isExpanded && (
                                            <div className="ml-6 space-y-2 pl-4 border-l border-white/10 bg-white/5 rounded-r-xl p-4 mt-2">
                                                {category.generators.map((generator) => (
                                                    <Link
                                                        key={generator.id}
                                                        href={getNavigationPath(generator.id)}
                                                        className="flex items-center space-x-3 p-3 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200 border border-transparent hover:border-white/10"
                                                        onClick={closeMenu}
                                                    >
                                                        <div className="w-6 h-6 flex items-center justify-center rounded-md bg-white/5 text-gray-400">
                                                            {generator.icon}
                                                        </div>
                                                        <span className="font-medium">{generator.name[locale]}</span>
                                                    </Link>
                                                ))}
                                                <Link
                                                    href={getNavigationPath(category.id)}
                                                    className="flex items-center justify-center space-x-2 p-3 rounded-lg text-sm text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium mt-3"
                                                    onClick={closeMenu}
                                                >
                                                    <span>{t('header.viewAllGeneratorsCount', { count: category.generators.length })}</span>
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Language Switcher */}
                            <div className="pt-4 border-t border-gray-800 flex items-center justify-between px-4">
                                <LanguageSwitcher variant="button" inMobileMenu={true} onLanguageChange={closeMenu} />
                                <ThemeToggle />
                            </div>
                        </nav>
                    </div>
                )}
            </Container>
        </header>
    );
}
